/** @jsxRuntime classic */
import { memo, useEffect, useMemo, useRef, forwardRef } from 'react';
import { string, number, bool, node, oneOfType, func } from 'prop-types';
import { css } from '@emotion/core';
import { renderHTML } from "../../utils";
import theme from "../../theme/settings";
const Paper = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  width,
  height,
  mapping,
  bg,
  className,
  square,
  scrollAble,
  refs,
  children,
  ...props
}, ref) => {
  if (!ref) {
    ref = useRef(null);
  }
  const PaperRoot = css`
            width: ${isNaN(width) ? width : width + 'px'};
            height: ${isNaN(height) ? height : height + 'px'};
            border: 1px solid ${theme.colors.line.normal};
            border-radius: 4px;
            background-color: ${bg};
            box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
            &.square {
                border-radius: 0;
            }
            &.scroll-able {
                max-height: ${isNaN(height) ? height : height + 'px'};
                overflow-y: auto;
                &::-webkit-scrollbar {
                    width: 8px;
                    background-color: #fff !important;
                    border-radius: 4px;
                }
                &::-webkit-scrollbar-thumb {
                    border-radius: 4px;
                    mix-blend-mode: normal;
                    background-color: ${theme.colors.dark12} !important;
                    background-clip: content-box;
                }
            }
        `;
  useEffect(() => {
    refs && refs(ref);
  }, []);
  useEffect(() => {
    if (square) {
      ref.current.classList.add('square');
    }
    return () => {
      if (square && ref.current) {
        ref.current.classList.remove('square');
      }
    };
  }, [square]);
  useEffect(() => {
    if (scrollAble && !ref.current.classList.contains('scroll-able')) {
      ref.current.classList.add('scroll-able');
    }
    return () => {
      if (scrollAble && ref.current) {
        ref.current.classList.remove('scroll-able');
      }
    };
  }, [scrollAble]);
  const PaperView = useMemo(() => renderHTML(children, mapping, {
    ref,
    css: PaperRoot,
    className: [className, scrollAble ? 'scroll-able' : ''].join(' ').trim(),
    ...props
  }), [children]);
  return PaperView;
}));
Paper.defaultProps = {
  width: 240,
  height: 240,
  mapping: 'div',
  bg: 'white',
  className: '',
  square: false,
  scrollAble: false
};
Paper.propTypes = {
  /** the width of Paper */
  width: oneOfType([string, number]),
  /** the height of Paper */
  height: oneOfType([string, number]),
  /** html tag to render */
  mapping: string,
  /** background color of Paper */
  bg: string,
  /** className of Paper component */
  className: string,
  /** rounded Paper if false */
  square: bool,
  /** Allow to roll if the height exceeds the limit */
  scrollAble: bool,
  /** the function to get ref of Paper */
  refs: func,
  /** content to display in Paper */
  children: node
};
export default Paper;